import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * ServiceUnavailableBanner — global, dismissible top banner shown when the
 * backend briefly can't reach its database (HTTP 503). Listens for the
 * `corvana:service-unavailable` event dispatched by authFetch/safeFetch. This
 * turns a transient MongoDB failover into a calm "please retry" prompt instead
 * of raw errors scattered across the UI. Auto-hides after 8s.
 */
export const ServiceUnavailableBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let hideTimer;
    const onEvent = () => {
      setVisible(true);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setVisible(false), 8000);
    };
    window.addEventListener('corvana:service-unavailable', onEvent);
    return () => {
      window.removeEventListener('corvana:service-unavailable', onEvent);
      clearTimeout(hideTimer);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      role="alert"
      data-testid="service-unavailable-banner"
      className="fixed top-0 inset-x-0 z-[9999] flex items-center justify-center gap-3 px-4 py-2.5 bg-amber-500 text-white text-sm font-medium shadow-md"
    >
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span>Corvana is briefly reconnecting to its database — please retry in a moment.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        data-testid="service-unavailable-retry"
        className="ml-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 text-xs font-semibold"
      >
        Retry
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setVisible(false)}
        data-testid="service-unavailable-dismiss"
        className="rounded-full hover:bg-white/20 transition-colors p-1"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ServiceUnavailableBanner;
